import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import Pagination from '@/components/pagination';
import { SortField, SortState } from './past-meetings.types';
import '@/assets/styles/views/solver/past-meetings.scss';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useSizes } from '@/utility/responsive.ts';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Modal } from '@/components';
import { SolvedQuestionResponse } from '@dopingtech/live_expert_bff_api_ts';
import { getMonthDateRange } from '@/utility';
import MobileDirection from '@/screens/solver/solve-question/states/mobile-direction.tsx';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { isWebView } from '@/utility/webview.ts';
import { endDateTimezone } from '@/constants';

const ITEMS_PER_PAGE = 10;

const _isWebView = isWebView();

const QuestionsAnsweredScreen: React.FC = () => {
	const [currentPage, setCurrentPage] = useState(1);
	const { isDesktop } = useSizes();
	const [sortState, setSortState] = useState<SortState>({
		field: 'date',
		direction: 'desc'
	});
	const [isActiveQuestionInspect, setIsActiveQuestionInspect] = useState(false);
	const [selectedQuestionImage, setSelectedQuestionImage] = useState<string>('');
	const [isOpenStudentNoteModal, setIsOpenStudentNoteModal] = useState<boolean>(false);
	const [selectedStudent, setSelectedStudent] = useState<SolvedQuestionResponse | null>(null);

	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState(dayjs());

	const { FeedbackControllerApi, InteractionControllerApi } = useApi();

	const { value: isActiveSolverAnsweredQuestionsMobileDirection, loading: isLoadingIsActiveSolverAnsweredQuestionsMobileDirection } = useFeatureFlag(
		CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_ANSWERED_QUESTIONS_MOBILE_DIRECTION,
		false
	);

	const _isActiveSolverAnsweredQuestionsMobileDirection = useMemo(
		() => isActiveSolverAnsweredQuestionsMobileDirection && !_isWebView,
		[_isWebView, isLoadingIsActiveSolverAnsweredQuestionsMobileDirection]
	);

	const getSortParams = () => {
		if (!sortState.field) return undefined;

		let sortField = '';
		if (sortState.field === 'date') {
			sortField = 'interactionEndDate';
		} else if (sortState.field === 'rating') {
			sortField = 'rating';
		}

		return [`${sortField},${sortState.direction}`];
	};

	const { data: pastMeetingData } = useQueryProvider(
		[QUERY_KEYS.getExpertSolvedQuestions, currentPage, sortState, selectedDate?.toString()],
		() =>
			InteractionControllerApi.findExpertInteractions(
				getMonthDateRange(selectedDate)?.startDate,
				getMonthDateRange(selectedDate, undefined, endDateTimezone)?.endDate,
				undefined,
				'SOLVER',
				currentPage - 1,
				ITEMS_PER_PAGE,
				getSortParams()
			),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false
		}
	);

	const { data: studentInteractionFeedback } = useQueryProvider(
		[QUERY_KEYS.getStudentInteractionFeedback, selectedStudent?.id],
		() => FeedbackControllerApi.getStudentInteractionFeedback(selectedStudent?.id),
		{
			select: ({ data }) => data.data,
			enabled: !!selectedStudent?.id,
			refetchOnWindowFocus: true
		}
	);

	const datePickerRef = useRef<HTMLDivElement | null>(null);

	const handleSort = (field: SortField) => {
		setSortState(prevState => ({
			field,
			direction: prevState.field === field && prevState.direction === 'asc' ? 'desc' : 'asc'
		}));
		setCurrentPage(1);
	};

	const meetingsData = useMemo(() => pastMeetingData?.content || [], [pastMeetingData]);

	const paginatedMeetings = meetingsData;

	const totalPages = pastMeetingData?.totalPages || 0;

	const getRatingColor = useCallback((rating: number): string => {
		if (rating >= 4) return '#FFD012';
		if (rating === 3) return '#E67E22';
		return 'var(--danger-text)';
	}, []);

	const renderRatingStars = useCallback(
		(rating: number) => {
			const ratingColor = getRatingColor(rating);
			return [...Array(5)].map((_, index) => (
				<i
					key={index}
					className={`icons-star ${index < rating ? '' : 'inactive'}`}
					style={index < rating ? { color: ratingColor } : undefined}
				/>
			));
		},
		[getRatingColor]
	);

	const getSortIcon = (field: SortField) => {
		if (sortState.field === field) {
			return <i className={`icons-alt-arrow-${sortState.direction === 'asc' ? 'down' : 'up'}`} />;
		}
		return <i className="icons-sort-from-top-to-bottom" />;
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
				setIsDatePickerOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const monthOptions = useMemo(() => {
		const options = [];
		const currentDate = dayjs();

		for (let i = 0; i < 12; i++) {
			const date = currentDate.subtract(i, 'month');
			options.push({
				value: date.format('YYYY-MM'),
				label: date.locale('tr').format('MMMM YYYY')
			});
		}
		return options;
	}, []);

	const toggleDatePicker = () => {
		setIsDatePickerOpen(!isDatePickerOpen);
	};

	const handleMonthSelect = (date: string) => {
		setSelectedDate(dayjs(date));
		setIsDatePickerOpen(false);
		setCurrentPage(1);
	};

	const handleQuestionImageClick = (questionImage: string) => {
		setSelectedQuestionImage(questionImage);
		setIsActiveQuestionInspect(true);
	};

	const handleStudentCommentClick = (meeting: SolvedQuestionResponse | null) => {
		setSelectedStudent(meeting);
		setIsOpenStudentNoteModal(true);
	};

	const formatDate = (date: string) => {
		const dateObj = dayjs(date);
		const now = dayjs();

		if (dateObj.isSame(now, 'day')) {
			return `Bugün, ${dateObj.format('HH:mm')}`;
		} else if (dateObj.diff(now, 'day') >= -7 && dateObj.diff(now, 'day') < 0) {
			return `${Math.abs(dateObj.diff(now, 'day'))} gün önce`;
		} else {
			return dateObj.format('D MMMM, HH:mm');
		}
	};

	const renderPastMeetingTable = () => {
		if (isDesktop) {
			return (
				<>
					{paginatedMeetings?.map((meeting, index) => (
						<div
							key={meeting.id}
							className="meeting-row">
							<div className="col-no">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</div>

							<div className="col-date">{formatDate(meeting.interactionEndDate)}</div>

							<div className="col-student">
								<img
									src={meeting?.studentAvatarUrl || `/api/placeholder/40/40`}
									alt={meeting.studentName}
								/>
								<div className="student-info">
									<div className="name">{`${meeting.studentName} ${meeting.studentSurname}`}</div>
									<div className="grade">{meeting.studentGradeName}</div>
								</div>
							</div>

							<div className="col-question">
								<img
									src={meeting?.questionImage}
									alt="Question"
									className="question-image"
									onClick={() => handleQuestionImageClick(meeting?.questionImage)}
								/>
							</div>

							<div className="col-rating">
								<div className="stars">{renderRatingStars(meeting?.rating || 0)}</div>
							</div>

							<div className="col-comment">
								{meeting?.rating && (
									<button
										className="comment-btn"
										onClick={() => handleStudentCommentClick(meeting)}>
										<i className="icons-file-text" />
									</button>
								)}
							</div>
							<div className="col-complaint">
								{meeting?.isRefund && (
									<div className="complaint-badge">
										<i className="icons-danger-circle" />
										İade
									</div>
								)}
							</div>
						</div>
					))}
				</>
			);
		} else {
			return (
				<div>
					{paginatedMeetings?.map(meeting => (
						<div
							key={meeting.id}
							className="mobile-meeting-card">
							<div className="mobile-card-header">
								<div className="student-section">
									<img
										src={meeting?.studentAvatarUrl || `/api/placeholder/40/40`}
										alt={meeting?.studentName}
										className="student-avatar"
									/>
									<div className="student-details">
										<div className="student-name">{`${meeting?.studentName} ${meeting?.studentSurname}`}</div>
										<div className="student-grade">{meeting?.studentGradeName}</div>
									</div>
								</div>
								<div>
									<div className="meeting-date">{formatDate(meeting.interactionEndDate)}</div>
									<div className="mobile-rating-section">
										{meeting.rating ? (
											<div className="rating-stars">{renderRatingStars(meeting.rating)}</div>
										) : (
											<div className="no-rating">
												<i className="icons-star inactive" />
												<i className="icons-star inactive" />
												<i className="icons-star inactive" />
												<i className="icons-star inactive" />
												<i className="icons-star inactive" />
											</div>
										)}
									</div>
								</div>
							</div>

							{meeting.interactionStatus === 'COMPLAINED' && (
								<div className="mobile-complaint-badge">
									<i className="icons-danger-circle" />
									<span>İade Edildi</span>
								</div>
							)}

							<div className="submission-section">
								<div className="submission-icon">
									<img
										src={meeting?.questionImage || `/assets/demo-video.svg`}
										alt="Submission"
										className="submission-image"
										onClick={() => handleQuestionImageClick(meeting?.questionImage || `/assets/demo-video.svg`)}
									/>
								</div>
								<div className="submission-info">
									<div className="submission-title">Gönderilme Tarihi</div>
									<div className="submission-date">{formatDate(meeting.interactionStartDate)}</div>
								</div>
								<button
									className="expand-button"
									onClick={() => handleQuestionImageClick(meeting?.questionImage || `/assets/demo-video.svg`)}>
									<i className="icons-search" />
								</button>
							</div>

							{meeting?.rating && (
								<div
									className="comment-section"
									onClick={() => handleStudentCommentClick(meeting)}>
									<div className="comment-icon">
										<i className="icons-file-text" />
									</div>
									<span className="comment-text">Öğrenci Yorumunu İncele</span>
								</div>
							)}
						</div>
					))}
				</div>
			);
		}
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	if (_isActiveSolverAnsweredQuestionsMobileDirection) {
		return <MobileDirection />;
	}

	return (
		<div className="questions-answered">
			<div className="page-header">
				<h1>Yanıtlanan Sorular</h1>
				<div
					className="date-picker"
					ref={datePickerRef}>
					<div
						className="date-trigger"
						onClick={toggleDatePicker}>
						<span className="current-month">{selectedDate.locale('tr').format('MMMM YYYY')}</span>
						<i className="icons-sort-from-top-to-bottom" />
					</div>

					{isDatePickerOpen && (
						<div className="month-dropdown">
							{monthOptions.map(month => (
								<div
									key={month.value}
									className={`month-option ${month.value === selectedDate.format('YYYY-MM') ? 'selected' : ''}`}
									onClick={() => handleMonthSelect(month.value)}>
									{month.label}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<div className="meetings-table">
				<div className="table-header desktop">
					<div className="col-no">No</div>
					<div
						className="col-date sortable"
						onClick={() => handleSort('date')}>
						<span>Tarih</span>
						{getSortIcon('date')}
					</div>
					<div className="col-student">Öğrenci</div>
					<div className="col-question">Soru</div>
					<div
						className="col-rating sortable"
						onClick={() => handleSort('rating')}>
						<span>Puan</span>
						{getSortIcon('rating')}
					</div>
					<div className="col-comment">Öğrenci Yorumu</div>
				</div>

				<div className="table-body">{renderPastMeetingTable()}</div>
			</div>

			{totalPages > 1 && (
				<div className="pagination-container">
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}

			<Lightbox
				open={isActiveQuestionInspect}
				close={() => setIsActiveQuestionInspect(false)}
				carousel={{ finite: true }}
				controller={{ closeOnBackdropClick: true }}
				render={{
					buttonPrev: () => null,
					buttonNext: () => null
				}}
				slides={[{ src: selectedQuestionImage, width: 920, height: 520 }]}
			/>
			<Modal
				isOpen={isOpenStudentNoteModal}
				onClose={() => setIsOpenStudentNoteModal(false)}
				closeOnEsc={true}
				closeOnClickOutside={true}
				variant={isDesktop ? 'centered' : 'bottom'}
				size="md"
				className="student-note-modal">
				<div
					className="close-icon"
					onClick={() => setIsOpenStudentNoteModal(false)}>
					<i className="icons-close" />
				</div>

				{selectedStudent && (
					<div className="student-note-modal-content">
						<div className="modal-header">
							<div className="student-info">
								<img
									src={selectedStudent.studentAvatarUrl || '/api/placeholder/60/60'}
									alt={selectedStudent.studentName}
									className="student-avatar"
								/>
								<div className="student-info-wrapper">
									<h3 className="student-name">{`${selectedStudent.studentName} ${selectedStudent.studentSurname}`}</h3>
									<p className="student-grade">{selectedStudent.studentGradeName}</p>
								</div>
							</div>
							<div className="rating-section">
								{selectedStudent.rating ? (
									<div className="stars">
										{[...Array(5)].map((_, index) => (
											<i
												key={index}
												className={`icons-star ${index < selectedStudent.rating ? '' : 'inactive'}`}
												style={{
													color: index < selectedStudent.rating ? '#FFD012' : '#E5E7EB',
													fontSize: '20px'
												}}
											/>
										))}
									</div>
								) : (
									<div className="no-rating">
										{[...Array(5)].map((_, index) => (
											<i
												key={index}
												className="icons-star inactive"
												style={{ color: '#E5E7EB', fontSize: '20px' }}
											/>
										))}
									</div>
								)}
							</div>
						</div>

						{studentInteractionFeedback?.optionSelections?.length > 0 && (
							<div className="display-buttons">
								{studentInteractionFeedback?.optionSelections.map(option => {
									return <div className="display-button">{option?.feedbackOption?.name}</div>;
								})}
							</div>
						)}

						{studentInteractionFeedback?.commentToExpert && (
							<div className="modal-content-area">
								<div className="comment-content">{studentInteractionFeedback.commentToExpert}</div>
							</div>
						)}
					</div>
				)}
			</Modal>
		</div>
	);
};

export default QuestionsAnsweredScreen;
